import {
  type ColumnDef,
  type ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import {
  createContext,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useState,
} from "react";

type DataTableContextProps<TData> = {
  table: ReturnType<typeof useReactTable<TData>>;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
/* biome-ignore lint/suspicious/noExplicitAny: The type is passed to the context */
const DataTableContext = createContext<DataTableContextProps<any> | undefined>(
  undefined
);

type DataTableInitialState = {
  columnVisibility?: VisibilityState;
  sorting?: SortingState;
  columnFilters?: ColumnFiltersState;
  pagination?: PaginationState;
};

type DataTableProviderProps<TData, TValue> = {
  children: ReactNode;
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  defaultPageSize?: number;
  className?: string;
  initialState?: DataTableInitialState;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (
    updater: VisibilityState | ((old: VisibilityState) => VisibilityState)
  ) => void;
  getRowId?: (originalRow: TData, index: number) => string;
  meta?: Record<string, unknown>;
};

export function DataTable<TData, TValue>({
  children,
  columns,
  data,
  className,
  defaultPageSize = 10,
  initialState,
  columnVisibility: controlledColumnVisibility,
  onColumnVisibilityChange: onControlledColumnVisibilityChange,
  getRowId,
  meta,
}: DataTableProviderProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>(
    initialState?.sorting ?? []
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    initialState?.columnFilters ?? []
  );
  const [internalColumnVisibility, setInternalColumnVisibility] =
    useState<VisibilityState>(
      controlledColumnVisibility ?? initialState?.columnVisibility ?? {}
    );

  // Use controlled state if provided, otherwise use internal state
  const columnVisibility =
    controlledColumnVisibility ?? internalColumnVisibility;
  const setColumnVisibility =
    onControlledColumnVisibilityChange ?? setInternalColumnVisibility;
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
    ...initialState?.pagination,
  });

  const [isMounted, setIsMounted] = useState(false);
  useLayoutEffect(() => {
    setIsMounted(true);
  }, []);

  const table = useReactTable<TData>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    getRowId,
    meta,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      pagination,
    },
  });

  if (!isMounted) {
    return null;
  }

  return (
    <DataTableContext.Provider value={{ table }}>
      <div className={className}>{children}</div>
    </DataTableContext.Provider>
  );
}

export function useDataTable() {
  const context = useContext(DataTableContext);
  if (!context) {
    throw new Error("useDataTable must be used within a DataTable");
  }
  return context.table;
}
